import type { IPageInfo } from "pages/notice/store/NoticeVo";

/**
 * 정산관리 내부 텝메뉴 타입
 */
export type CalculateActiveTab = ActiveType.CP | ActiveType.CON | ActiveType.PN;
export type CalculateContentSubTab = "cp" | "msn";
export type CalculateMediaSubTab = "sales";
export type CalculatePartnersSubTab = "pp" | "partners";

/**
 * 대분류 탭 타입
 */
export enum ActiveType {
  CP = "PROVIDER",
  CON = "MEDIA",
  PN = "PARTNERS",
}

export interface IResSalaesList {
  list: IExpectListVo[] | ISettlementListVo[];
  page_info?: IPageInfo;
}

/**
 * 예상수익 리스트 데이터 Vo
 */
export interface IExpectListVo {
  name: string;
  view_us?: number;
  msn?: number;
  expect: number;
}

/**
 * 정산금 리스트 데이터 Vo
 */
export interface ISettlementListVo {
  name: string;
  settlement: number;

  //엑셀 불러오기 시에만 사용할 컬럼
  channel_id?: string;
  page_count?: number;
}

/**
 * 지급 여부
 */
export enum payStatus {
  ok = "COMPLETED",
  not = "IN_COMPLETED",
  diff = "DIFFERENT",
}

/**
 * 세금계산서 발행 여부
 */
export enum taxStatus {
  ok = "ISSUE",
  not = "NOT_ISSUE",
  diff = "DIFFERENT",
}

/**
 * 역세금계산서 승인여부
 */
export enum approvalStatus {
  ok = "APPROVED",
  not = "NOT_APPROVED",
}

/**
 * @interface IResTotalCalculateData 정산 탭 정산자동화를 위한 리스트 (초기화면 리스트)
 * @property {ICalculateTotalTopInfoVo} info 리스트 페이지 상단 텍스트 토탈 정보
 * @property {IContentsChanneTotalVo} total 그리드 리스트 합계 정보
 * @property {IContentsChannelGirdListVo[]} list 정산 관련 리스트
 */
export interface IResTotalCalculateData {
  info: ICalculateTotalTopInfoVo;
  total: IContentsChanneTotalVo;
  list: IContentsChannelGirdListVo[];
}

/**
 * @interface ICalculateTotalTopInfoVo 정산금, 이익금, 최종 이익금 등 정보
 * @property {number} pp_calculate pp 이익금
 * @property {number} msn_calculate MSN 이익금
 * @property {number} cp_settlement cp 정산금
 * @property {number} pp_settlement pp 정산금
 * @property {number} total_sales 총 매출
 * @property {number} total_page_view 총 조회수
 * @property {number} total_calculate 최종 이익금
 */
export interface ICalculateTotalTopInfoVo {
  pp_calculate?: number; //pp 이익금
  msn_calculate?: number; //MSN 이익금
  cp_settlement?: number; //cp 정산금
  pp_settlement?: number; //pp 정산금
  total_sales?: number; // 총 매출
  total_page_view?: number; // 총 조회수
  total_calculate?: number; // 최종 이익금
}

/**
 * @interface IContentsChannelGirdListVo 콘텐츠 탭 grid 리스트 내 채널별 데이터(pp 제외)
 * @property {number} user_id 사업자 id
 * @property {string} business_name 사업자명
 * @property {taxStatus} tax_status 세금계산서 발행여부 (ISSUE: 발행, NOT_ISSUE: 미발행, 금액상이: DIFF)
 * @property {payStatus} pay_status 정산금 지급여부 (COMPLETED: 지급완료, IN_COMPLETED: 미지급, 금액상이: DIFF)
 * @property {approvalStatus} approval_status 역발행계산서 승인여부 (APPROVED: 승인, NOT_APPROVED: 미승인)
 * @property {string} pay_date 정산금 지급일자
 * @property {string} message 메모
 * @property {IContentsChanneGridDetail[]} list 채널별 내부 데이터
 */
export interface IContentsChannelGirdListVo {
  user_id: number; // 사업자 id
  business_name: string; // 사업자명
  tax_status: taxStatus; // 세금계산서 발행여부
  pay_status: payStatus; // 정산금 지급여부
  approval_status: approvalStatus; // 역발행계산서 승인여부
  pay_date?: string; // 정산금 지급일자
  message: string; // 메모
  list: IContentsChanneGridDetail[]; //채널별 내부 데이터

  //엑셀 불러오기 시에만 사용할 컬럼(미디어)
  channel_id?: string;
  page_count?: number;
}

/**
 * @interface IContentsChanneDetail 사업자명에 묶인 채넓별 내부 데이터? (채널명, 조회수 등)
 * @property {number} service_register_id 서비스 등록 id (채널 id)
 * @property {string} code 채널 코드
 * @property {string} name 채널명
 * @property {string} brand_info 브랜드 정보
 * @property {number} page_view 조회수
 * @property {number} sales 발생 매출
 * @property {number} ratio 요율
 * @property {number} origin_settlement 발생 매출인데, 요율 계산을 위한 origin 정산금
 * @property {number} settlement 요율 적용된 정산금
 * @property {number} add_settlement 추가 정산금
 * @property {number} calculate 최종 합계
 * @property {number} unit_price 단가
 */
export interface IContentsChanneGridDetail {
  service_register_id: number;
  code: string;
  name: string;
  brand_info?: string;
  page_view: number;
  sales: number;
  ratio: number;
  origin_settlement?: number;
  settlement: number;
  add_settlement: number;
  calculate: number;
  unit_price: number;
}

/**
 * @interface IContentsChanneTotalVo 정산 합계(total) data
 * @property {number} page_view 조회수
 * @property {number} sales 발생 매출
 * @property {number} cp_settlement cp 정산금
 * @property {number} add_settlement 추가 정산금
 * @property {number} calculate 최종 합계
 * @property {number} tax 세액(음원)
 */
export interface IContentsChanneTotalVo {
  page_view: number;
  sales: number;
  cp_settlement?: number;
  add_settlement?: number;
  calculate: number;
  tax?: number;
}
/**
 * 음원영상 탭 - 매출 정산내역 저장시 보낼 파라미터
 */
export interface IMediaExcelListFetc {
  year: number;
  month: number;
  service_type: string;
  data: ISettlementListVo[];
}

/**
 * @interface IGridRowUpdateData 정산 테이블 수정할 때 리덕스로 보낼 데이터
 * @property {number} user_id user_id
 * @property {number} code code
 * @property {number} type 변경 된 타입(요율, 정산금 등..)
 * @property {number} value 리덕스 보낼 값 (요율값, 추가정산금 등..)
 * @property {number} row_num 변경 될 row 번호
 */
export interface IGridRowUpdateData {
  user_id?: number;
  code?: string;
  type?: string;
  value?: string | number;
  row_num?: number;
}
